'use client';

import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getWorkloadMetrics, type WorkloadMetricsInterval } from '@/lib/urm-api';
import type { WorkloadMetricsSnapshot } from '@/lib/metrics-snapshot';
import { WORKLOAD_PAGE_LIMIT, WORKLOAD_REFRESH_INTERVAL_MS } from '../types';

interface UseWorkloadMetricsArgs {
  durationMs: number;
  endMs: number | null;
  autoRefresh: boolean;
  snapshot?: WorkloadMetricsSnapshot | null;
}

interface UseWorkloadMetricsResult {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
  error: string | null;
  lastUpdatedMs: number | null;
  loadedFromMs: number | null;
  loadedToMs: number | null;
  refetch: () => void;
}

// The upstream endpoint caps pages at 20 intervals. Three days of 30-second
// intervals can therefore require 432 pages when the full window is retained.
const MAX_PAGES_PER_LOAD = 500;

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useWorkloadMetrics({
  durationMs,
  endMs,
  autoRefresh,
  snapshot = null,
}: UseWorkloadMetricsArgs): UseWorkloadMetricsResult {
  const [intervals, setIntervals] = useState<WorkloadMetricsInterval[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedMs, setLastUpdatedMs] = useState<number | null>(null);
  const [loadedFromMs, setLoadedFromMs] = useState<number | null>(null);
  const [loadedToMs, setLoadedToMs] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    if (snapshot !== null) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const runId = ++runIdRef.current;
    const isCurrent = () => runIdRef.current === runId && !controller.signal.aborted;
    const toMs = endMs ?? Date.now();
    const fromMs = toMs - durationMs;

    setLoading(true);
    setError(null);
    try {
      const collected: WorkloadMetricsInterval[] = [];
      let page = await getWorkloadMetrics(
        { from: fromMs, to: toMs, limit: WORKLOAD_PAGE_LIMIT },
        controller.signal,
      );
      collected.push(...page.intervals);
      let cursor = page.next_cursor;
      let pageCount = 1;

      while (page.truncated) {
        if (!cursor) throw new Error('Workload metrics pagination stopped without a cursor');
        if (pageCount >= MAX_PAGES_PER_LOAD) throw new Error('Workload metrics response exceeded the page limit');
        const previousCursor = cursor;
        page = await getWorkloadMetrics(
          { startAfter: cursor, to: toMs, limit: WORKLOAD_PAGE_LIMIT },
          controller.signal,
        );
        collected.push(...page.intervals);
        cursor = page.next_cursor;
        pageCount += 1;
        if (page.truncated && cursor === previousCursor) {
          throw new Error('Workload metrics pagination cursor did not advance');
        }
      }

      if (!isCurrent()) return;
      const unique = new Map<number, WorkloadMetricsInterval>();
      for (const interval of collected) unique.set(interval.start_ms, interval);
      setIntervals(Array.from(unique.values()).sort((a, b) => a.start_ms - b.start_ms));
      setLoadedFromMs(fromMs);
      setLoadedToMs(toMs);
      setLastUpdatedMs(Date.now());
    } catch (caught: unknown) {
      if (!isCurrent() || isAbortError(caught)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [durationMs, endMs, snapshot]);

  useEffect(() => {
    startTransition(() => { void load(); });
    return () => {
      runIdRef.current += 1;
      abortRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    if (snapshot !== null || !autoRefresh || endMs !== null) return;
    const timer = window.setInterval(() => { void load(); }, WORKLOAD_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [autoRefresh, endMs, load, snapshot]);

  const refetch = useCallback(() => { void load(); }, [load]);
  const snapshotResult = useMemo(() => {
    if (snapshot === null) return null;
    const snapshotToMs = endMs ?? snapshot.loadedToMs;
    const snapshotFromMs = snapshotToMs - durationMs;
    return {
      intervals: snapshot.intervals.filter((interval) => (
        interval.end_ms >= snapshotFromMs && interval.start_ms <= snapshotToMs
      )),
      loadedFromMs: snapshotFromMs,
      loadedToMs: snapshotToMs,
    };
  }, [durationMs, endMs, snapshot]);

  if (snapshot !== null && snapshotResult !== null) {
    return {
      intervals: snapshotResult.intervals,
      loading: false,
      error: null,
      lastUpdatedMs: snapshot.lastUpdatedMs,
      loadedFromMs: snapshotResult.loadedFromMs,
      loadedToMs: snapshotResult.loadedToMs,
      refetch,
    };
  }

  return { intervals, loading, error, lastUpdatedMs, loadedFromMs, loadedToMs, refetch };
}
